import React, {useEffect, useState} from 'react';
import {Modal, Button, Input, Textarea, Badge, Checkbox} from '@salesforce/design-system-react';
import { isValidUrl } from '../../../../components/utils/common';

type EditReasonModalProps = {
    isOpen: boolean;
    toggleModal: (isOpen: boolean) => void;
    onCancel: () => void;
    reasonData: any;
    onUpdate: (data: ReasonData) => void;
    majorReason?: any
};

type ReasonData = {
    label: string;
    description: string;
    url: string;
    note: string;
    id?: string;
    parent_id?: string;
    value?: string;
    sort_order?: number;
    is_active: boolean;
    show_recommended_actions: boolean;
};

const EditReasonModal: React.FC<EditReasonModalProps> = ({
   isOpen,
   toggleModal,
   onCancel,
   reasonData,
   onUpdate,
   majorReason,
}) => {
    const [label, setLabel] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [url, setUrl] = useState<string>('');
    const [note, setNote] = useState<string>('');
    const [parent_id, setParentId] = useState<string>('');
    const [id, setId] = useState<string>('');
    const [sort_order, setSortOrder] = useState<number>(0);
    const [show_recommended_actions, setShowRecommendedActions] = useState<boolean>(false);

    const [urlError, setUrlError] = useState<string>("");
    const [isEditButtonDisabled, setIsEditButtonDisabled] = useState(false);

    const isMinor = !!parent_id;
    const modalTitle = isMinor ? 'Edit Minor Reason' : 'Edit Major Reason';

    useEffect(() => {
        if (reasonData) {
            setLabel(reasonData.label || '');
            setDescription(reasonData.description || '');
            setUrl(reasonData.url || '');
            setNote(reasonData.note || '');
            setParentId(reasonData.parent_id || '');
            setId(reasonData.id || '');
            setSortOrder(reasonData.sort_order || 0);
            setShowRecommendedActions(reasonData.show_recommended_actions);
        } else {
            setLabel("");
            setDescription("");
            setUrl("");
            setNote("");
            setUrlError("");
            setParentId("");
            setId("");
            setSortOrder(0);
            setShowRecommendedActions(false);
        }
    }, [reasonData]);

    const handleLabelChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setLabel(event.target.value);
    };

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(event.target.value);
    };

    const handleURLChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setUrl(event.target.value);
    };

    const handleNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNote(event.target.value);
    };

    const handleSortOrderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSortOrder(parseInt(event.target.value) || 0);
    };



    const handleUpdate = () => {
        toggleModal(false);
        let newValue = label
        let type = "MAJOR"
        if (isMinor) {
            newValue = majorReason.value + " - " + label
            type = "MINOR"
        }
        const data = {
            id: id,
            parent_id: parent_id || "",
            label: label,
            description: description,
            value: newValue,
            url: url,
            note: note,
            type: type,
            sort_order: sort_order,
            is_active: true,
            show_recommended_actions: show_recommended_actions,
        }
        onUpdate(data);
    };

    // Run validation on input change
    useEffect(() => {
        const urlValidation = isValidUrl(url || '');
        setUrlError(urlValidation.errorMessage);
        const allValid = label.trim() && description.trim() && urlValidation.isValid;
        setIsEditButtonDisabled(!allValid);
    }, [label, description, url]);

    return (
        <Modal
            isOpen={isOpen}
            disableClose
            prompt="warning"
            onRequestClose={() => toggleModal(false)}
            heading={modalTitle}
            footer={[
                <Button label="Update" variant="brand" onClick={handleUpdate} disabled={isEditButtonDisabled}/>,
                <Button label="Cancel" variant="neutral" onClick={onCancel}/>,
            ]}
        >
            <div className="slds-p-around--xx-small">
                {majorReason && (
                    <div>
                        <div className="slds-form-element slds-m-bottom_medium">
                            <label className="slds-form-element__label">Major Reason</label>
                            <div className="slds-form-element__control">
                                <span className="slds-text-body_regular slds-p-around_xx-small slds-theme_shade">
                                    {majorReason.label}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
                <Input
                    id="edit_reason_label"
                    label="Label"
                    value={label}
                    required={true}
                    onChange={handleLabelChange}
                    placeholder="Enter a valid label"
                />
                <Textarea
                    id="edit_reason_description"
                    label="Description"
                    value={description}
                    required={true}
                    onChange={handleDescriptionChange}
                    placeholder="Enter a valid description"
                />
                {majorReason && (
                    <div>
                        <Input
                            id="edit_reason_url"
                            label="URL"
                            value={url}
                            onChange={handleURLChange}
                            type="url"
                            placeholder="Enter a valid URL"
                            errorText={urlError}
                        />
                        <Textarea
                            id="edit_reason_note"
                            label="Note"
                            value={note}
                            onChange={handleNoteChange}
                            placeholder="Enter a valid note"
                        />
                    </div>
                )}
                <Input
                    id="edit_reason_sort_order"
                    label="Sort Order"
                    value={sort_order}
                    onChange={handleSortOrderChange}
                    type="number"
                    placeholder="Enter sort order"
                />
                {majorReason && <Checkbox
                    assistiveText={{
                        label: 'Show Recommended Actions',
                    }}
                    id="edit_reason_show_recommended_actions"
                    labels={{
                        label: 'Show Recommended Actions',
                    }}
                    checked={show_recommended_actions}
                    onChange={(e: any) => {
                        if (e.target.checked) {
                            setShowRecommendedActions(true)
                        } else {
                            setShowRecommendedActions(false)
                        }

                    }}
                />}
            </div>
        </Modal>
    );
};

export default EditReasonModal;
